// src/components/AdminUsers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const BASE_ROLES = [
  "admin",
  "warehouse",
  "stocktake",
  "despatch",
  "driver",
  "production",
  "stretch",
  "maintenance",
  "maintmanager",
  "operator",
  "internal",
  "office",
  "returns",
  "sales",
  "salesrep",
  "tablet",
  "tabletadmin",
  "notice",
  "fitting",
  "manager",
  "liam",
];

const normaliseRole = (role) => String(role || "").trim().toLowerCase();
const normaliseRoles = (list) => Array.from(new Set((list || []).map(normaliseRole).filter(Boolean))).sort();

const rolesFromClaim = (rolesClaim) => {
  if (rolesClaim && typeof rolesClaim === "object" && !Array.isArray(rolesClaim)) {
    return Object.entries(rolesClaim)
      .filter(([, enabled]) => !!enabled)
      .map(([role]) => role)
      .sort();
  }

  return Array.isArray(rolesClaim) ? normaliseRoles(rolesClaim) : [];
};

const sameSet = (a = [], b = []) => {
  const left = normaliseRoles(a);
  const right = normaliseRoles(b);
  if (left.length !== right.length) return false;
  return left.every((role) => right.includes(role));
};

export default function AdminUsers() {
  const db = getFirestore();
  const functions = getFunctions(undefined, "europe-west2");
  const auth = getAuth();

  const PAGE_SIZE = 50;

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState(["office"]);
  const [customRole, setCustomRole] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [migrate, setMigrate] = useState(true);
  const [sendSetupEmail, setSendSetupEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const [users, setUsers] = useState([]);
  const [myTokenRoles, setMyTokenRoles] = useState([]);
  const [claimsByUid, setClaimsByUid] = useState({});
  const [claimsLoading, setClaimsLoading] = useState(false);

  const [busyAll, setBusyAll] = useState(false);
  const [busyRow, setBusyRow] = useState({});
  const [roleDrafts, setRoleDrafts] = useState({});
  const [resetBusy, setResetBusy] = useState({});
  const [resetLinks, setResetLinks] = useState({});

  const setRowBusy = (uid, value) => setBusyRow((prev) => ({ ...prev, [uid]: value }));
  const setResetRowBusy = (uid, value) => setResetBusy((prev) => ({ ...prev, [uid]: value }));

  useEffect(() => {
    const qy = query(collection(db, "users"), orderBy("createdAt", "desc"), limit(PAGE_SIZE));
    const unsub = onSnapshot(qy, (snap) => {
      setUsers(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });
    return () => unsub();
  }, [db]);

  useEffect(() => {
    setRoleDrafts((prev) => {
      const next = { ...prev };
      let changed = false;

      users.forEach((user) => {
        const dbRoles = normaliseRoles(user.roles);
        const draft = next[user.id];

        if (!draft) {
          next[user.id] = { roles: dbRoles, sourceRoles: dbRoles, customRole: "" };
          changed = true;
        } else {
          const sourceRoles = draft.sourceRoles || dbRoles;
          const hasLocalChanges = !sameSet(draft.roles, sourceRoles);

          if (!hasLocalChanges && !sameSet(sourceRoles, dbRoles)) {
            next[user.id] = { ...draft, roles: dbRoles, sourceRoles: dbRoles };
            changed = true;
          } else if (!draft.sourceRoles) {
            next[user.id] = { ...draft, sourceRoles };
            changed = true;
          }
        }
      });

      Object.keys(next).forEach((uid) => {
        if (!users.some((user) => user.id === uid)) {
          delete next[uid];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [users]);

  useEffect(() => {
    (async () => {
      if (!auth.currentUser) {
        setMyTokenRoles([]);
        return;
      }

      const res = await auth.currentUser.getIdTokenResult(true);
      setMyTokenRoles(rolesFromClaim(res.claims?.roles));
    })();
  }, [auth]);

  const allRoleOptions = useMemo(() => {
    return normaliseRoles([
      ...BASE_ROLES,
      ...roles,
      ...users.flatMap((user) => (Array.isArray(user.roles) ? user.roles : [])),
      ...Object.values(roleDrafts).flatMap((draft) => draft.roles || []),
    ]);
  }, [roles, roleDrafts, users]);

  const selectedCreateRoles = useMemo(() => normaliseRoles(roles), [roles]);
  const canSubmit = email.trim().length > 3 && selectedCreateRoles.length > 0 && !submitting;

  const refreshMyTokenRoles = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.getIdToken(true);
    const res = await auth.currentUser.getIdTokenResult();
    setMyTokenRoles(rolesFromClaim(res.claims?.roles));
  };

  const toggleRole = (role) => {
    const nextRole = normaliseRole(role);
    if (!nextRole) return;
    setRoles((prev) => (
      prev.includes(nextRole)
        ? prev.filter((item) => item !== nextRole)
        : normaliseRoles([...prev, nextRole])
    ));
  };

  const addCustomRoleToCreate = () => {
    const role = normaliseRole(customRole);
    if (!role) return;
    setRoles((prev) => normaliseRoles([...prev, role]));
    setCustomRole("");
  };

  const toggleDraftRole = (uid, role) => {
    const nextRole = normaliseRole(role);
    if (!nextRole) return;

    setRoleDrafts((prev) => {
      const draft = prev[uid] || { roles: [], sourceRoles: [], customRole: "" };
      const draftRoles = draft.roles.includes(nextRole)
        ? draft.roles.filter((item) => item !== nextRole)
        : normaliseRoles([...draft.roles, nextRole]);
      return { ...prev, [uid]: { ...draft, roles: draftRoles } };
    });
  };

  const setDraftCustomRole = (uid, value) => {
    setRoleDrafts((prev) => {
      const draft = prev[uid] || { roles: [], sourceRoles: [], customRole: "" };
      return { ...prev, [uid]: { ...draft, customRole: value } };
    });
  };

  const addDraftCustomRole = (uid) => {
    setRoleDrafts((prev) => {
      const draft = prev[uid] || { roles: [], sourceRoles: [], customRole: "" };
      const role = normaliseRole(draft.customRole);
      if (!role) return prev;
      return { ...prev, [uid]: { ...draft, roles: normaliseRoles([...draft.roles, role]), customRole: "" } };
    });
  };

  const resetDraftRoles = (uid, dbRoles) => {
    const rolesForDb = normaliseRoles(dbRoles);
    setRoleDrafts((prev) => ({ ...prev, [uid]: { roles: rolesForDb, sourceRoles: rolesForDb, customRole: "" } }));
  };

  const loadClaims = async (uids) => {
    if (!uids || !uids.length) return;

    try {
      setClaimsLoading(true);
      const fn = httpsCallable(functions, "adminGetClaims");
      const { data } = await fn({ uids });
      setClaimsByUid((prev) => ({ ...prev, ...(data?.claimsByUid || {}) }));
    } catch (e) {
      console.error(e);
      setError(e?.message || String(e));
    } finally {
      setClaimsLoading(false);
    }
  };

  const loadAllClaims = () => loadClaims(users.map((user) => user.id));
  const loadRowClaims = (uid) => loadClaims([uid]);

  const callSetClaims = async ({ uids, mode = "replace", dryRun = false }) => {
    const fn = httpsCallable(functions, "adminSetClaimsFromDb");
    return fn({ uids, mode, dryRun });
  };

  const applyAll = async (mode = "replace", dryRun = false) => {
    const uids = users.map((user) => user.id);
    if (!uids.length) return;

    try {
      setBusyAll(true);
      setError("");
      await callSetClaims({ uids, mode, dryRun });
      if (!dryRun) await loadClaims(uids);
    } catch (e) {
      console.error(e);
      setError(e?.message || String(e));
    } finally {
      setBusyAll(false);
    }
  };

  const applyOne = async (uid, mode = "replace", dryRun = false) => {
    try {
      setRowBusy(uid, true);
      setError("");
      await callSetClaims({ uids: [uid], mode, dryRun });
      if (!dryRun) await loadClaims([uid]);
    } catch (e) {
      console.error(e);
      setError(e?.message || String(e));
    } finally {
      setRowBusy(uid, false);
    }
  };

  const saveRolesForUser = async (uid) => {
    const draft = roleDrafts[uid] || { roles: [] };
    const nextRoles = normaliseRoles(draft.roles);

    try {
      setRowBusy(uid, true);
      setError("");
      const fn = httpsCallable(functions, "adminUpdateUserRoles");
      await fn({ uid, roles: nextRoles });
      setRoleDrafts((prev) => ({ ...prev, [uid]: { roles: nextRoles, sourceRoles: nextRoles, customRole: "" } }));
      await loadClaims([uid]);
      if (auth.currentUser?.uid === uid) await refreshMyTokenRoles();
    } catch (e) {
      console.error(e);
      setError(e?.message || String(e));
    } finally {
      setRowBusy(uid, false);
    }
  };

  const sendResetEmail = async ({ uid, email: targetEmail, continueUrl }) => {
    const fn = httpsCallable(functions, "adminGenerateResetLink");
    const { data } = await fn({ uid, email: targetEmail, continueUrl, sendEmail: true });
    return data;
  };

  const createUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setResult(null);

    try {
      const call = httpsCallable(functions, "adminCreateUser");
      const payload = {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        roles: selectedCreateRoles,
        migrateFromOldUsers: !!migrate,
        sendSetupEmail: !!sendSetupEmail,
        ...(tempPassword.trim() ? { tempPassword: tempPassword.trim() } : {}),
      };
      const { data } = await call(payload);
      setResult(data);

      if (auth.currentUser && data?.uid === auth.currentUser.uid) {
        await refreshMyTokenRoles();
      }

      setEmail("");
      setName("");
      setTempPassword("");
      setCustomRole("");
    } catch (err) {
      console.error(err);
      setError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">Admin - Users</h1>

      <div className="mb-6 text-sm">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded border">
          <span className="font-semibold">Your token roles:</span>
          {myTokenRoles.length ? (
            <div className="flex flex-wrap gap-1">
              {myTokenRoles.map((role) => (
                <span key={role} className="px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs">
                  {role}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-500">none</span>
          )}
          <button type="button" className="ml-3 px-2 py-1 text-xs rounded border hover:bg-gray-100" onClick={refreshMyTokenRoles}>
            Refresh token
          </button>
        </div>
      </div>

      <form onSubmit={createUser} className="bg-white border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Create / Upsert User</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex flex-col">
            <span className="text-sm text-gray-600 mb-1">Email (Auth key)</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company.com" className="border rounded px-3 py-2" />
          </label>
          <label className="flex flex-col">
            <span className="text-sm text-gray-600 mb-1">Name (displayName)</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="border rounded px-3 py-2" />
          </label>
          <label className="flex flex-col">
            <span className="text-sm text-gray-600 mb-1">Temporary password (optional)</span>
            <input
              type="text"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
              placeholder="Leave blank to email setup link"
              className="border rounded px-3 py-2"
            />
          </label>
          <div className="mt-6 space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={migrate} onChange={(e) => setMigrate(e.target.checked)} />
              <span className="text-sm">Migrate legacy prefs from <code>Users/{"{email}"}</code> if present</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={sendSetupEmail}
                onChange={(e) => setSendSetupEmail(e.target.checked)}
                disabled={!!tempPassword.trim()}
              />
              <span className="text-sm">Email setup / forgotten password link when no temporary password is set</span>
            </label>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-sm text-gray-600 mb-2">Roles</div>
          <div className="flex flex-wrap gap-2">
            {allRoleOptions.map((role) => {
              const active = selectedCreateRoles.includes(role);
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => toggleRole(role)}
                  className={`text-xs px-2 py-1 rounded border ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"}`}
                >
                  {role}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={customRole}
              onChange={(e) => setCustomRole(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomRoleToCreate();
                }
              }}
              placeholder="Add custom role"
              className="border rounded px-3 py-2 text-sm"
            />
            <button type="button" onClick={addCustomRoleToCreate} className="px-3 py-2 text-sm rounded border hover:bg-gray-100">
              Add role
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={!canSubmit} className="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50">
            {submitting ? "Saving..." : "Create / Update"}
          </button>
          {error && <span className="text-red-600 text-sm">{error}</span>}
        </div>

        {result && (
          <div className="mt-4 border rounded p-3 bg-green-50 text-sm">
            <div className="font-semibold">OK</div>
            <div>uid: <code>{result.uid}</code></div>
            <div>email: <code>{result.email}</code></div>
            {result.passwordResetLink ? (
              <div className="mt-2">
                <div>{result.passwordResetEmailSent ? "Setup email sent. Link generated:" : "Password reset link generated:"}</div>
                <Copyable value={result.passwordResetLink} />
              </div>
            ) : (
              <div className="mt-2 text-gray-700">You set a temporary password. Ask the user to sign in and change it.</div>
            )}
          </div>
        )}
      </form>

      <div className="bg-white border rounded-lg">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="font-semibold">Existing Users</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={loadAllClaims} className="text-xs px-2 py-1 rounded border hover:bg-gray-100" disabled={claimsLoading || users.length === 0}>
              {claimsLoading ? "Loading claims..." : "Load all claims"}
            </button>
            <button type="button" onClick={() => applyAll("replace", true)} className="text-xs px-2 py-1 rounded border hover:bg-gray-100" disabled={busyAll || users.length === 0} title="Preview: show what would be set">
              {busyAll ? "..." : "Preview all"}
            </button>
            <button type="button" onClick={() => applyAll("replace", false)} className="text-xs px-2 py-1 rounded border hover:bg-gray-100" disabled={busyAll || users.length === 0} title="Replace token roles with Firestore roles">
              {busyAll ? "..." : "Apply all (replace)"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Firestore Roles</th>
                <th className="px-3 py-2">Claims (token)</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td className="px-3 py-3 text-gray-500" colSpan={6}>No users yet.</td></tr>
              )}
              {users.map((user) => {
                const dbRoles = normaliseRoles(user.roles);
                const draft = roleDrafts[user.id] || { roles: dbRoles, sourceRoles: dbRoles, customRole: "" };
                const draftRoles = normaliseRoles(draft.roles);
                const claimRoles = claimsByUid[user.id] || [];
                const inSync = claimRoles.length ? sameSet(dbRoles, claimRoles) : null;
                const rolesChanged = !sameSet(dbRoles, draftRoles);
                const rowBusy = !!busyRow[user.id];

                return (
                  <tr key={user.id} className="border-t align-top">
                    <td className="px-3 py-2">{user.name || "-"}</td>
                    <td className="px-3 py-2">{user.email || "-"}</td>
                    <td className="px-3 py-2">
                      <div className="min-w-72 space-y-2">
                        <div className="flex flex-wrap gap-1">
                          {allRoleOptions.map((role) => {
                            const active = draftRoles.includes(role);
                            return (
                              <button
                                key={role}
                                type="button"
                                onClick={() => toggleDraftRole(user.id, role)}
                                disabled={rowBusy}
                                className={`px-2 py-0.5 text-xs rounded border ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"}`}
                              >
                                {role}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={draft.customRole || ""}
                            onChange={(e) => setDraftCustomRole(user.id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addDraftCustomRole(user.id);
                              }
                            }}
                            placeholder="Custom role"
                            className="min-w-0 flex-1 border rounded px-2 py-1 text-xs"
                            disabled={rowBusy}
                          />
                          <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100" onClick={() => addDraftCustomRole(user.id)} disabled={rowBusy}>
                            Add
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100 disabled:opacity-50" onClick={() => saveRolesForUser(user.id)} disabled={rowBusy || !rolesChanged || draftRoles.length === 0}>
                            {rowBusy ? "Saving..." : "Save roles"}
                          </button>
                          <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100 disabled:opacity-50" onClick={() => resetDraftRoles(user.id, dbRoles)} disabled={rowBusy || !rolesChanged}>
                            Reset
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {claimRoles.length ? (
                        <div className="flex flex-wrap gap-1">
                          {claimRoles.map((role) => (
                            <span key={role} className="px-2 py-0.5 text-xs rounded bg-blue-50 border border-blue-200 text-blue-700">
                              {role}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {inSync === null ? (
                        <span className="text-xs text-gray-500">unknown</span>
                      ) : inSync ? (
                        <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">in sync</span>
                      ) : (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">mismatch</span>
                      )}
                    </td>
                    <td className="px-3 py-2 w-64">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100" onClick={() => loadRowClaims(user.id)} disabled={rowBusy}>
                          {rowBusy ? "..." : "Load claims"}
                        </button>
                        <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100" onClick={() => applyOne(user.id, "replace", true)} disabled={rowBusy} title="Dry run">
                          Preview
                        </button>
                        <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100" onClick={() => applyOne(user.id, "replace", false)} disabled={rowBusy} title="Replace token roles with Firestore roles">
                          Apply
                        </button>
                        <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100" onClick={() => applyOne(user.id, "merge", false)} disabled={rowBusy} title="Union of token and Firestore roles">
                          Merge
                        </button>
                        <button type="button" className="text-xs px-2 py-1 rounded border hover:bg-gray-100" onClick={() => applyOne(user.id, "clear", false)} disabled={rowBusy} title="Clear roles in token, keeping other claims">
                          Clear
                        </button>
                        <button
                          type="button"
                          className="text-xs px-2 py-1 rounded border hover:bg-gray-100"
                          disabled={!!resetBusy[user.id]}
                          onClick={async () => {
                            setError("");
                            setResetRowBusy(user.id, true);
                            try {
                              const reset = await sendResetEmail({ uid: user.id, email: user.email });
                              setResetLinks((prev) => ({ ...prev, [user.id]: reset?.link || "" }));
                            } catch (e) {
                              console.error(e);
                              setError(e?.message || String(e));
                            } finally {
                              setResetRowBusy(user.id, false);
                            }
                          }}
                          title="Email password reset link for this user"
                        >
                          {resetBusy[user.id] ? "..." : "Email reset"}
                        </button>
                      </div>

                      {resetLinks[user.id] && (
                        <div className="mt-2">
                          <div className="text-xs text-green-700 mb-1">Reset email sent. Copy link if needed:</div>
                          <Copyable value={resetLinks[user.id]} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        Note: after applying roles, users need to sign out/in or refresh their token to see changes in the UI.
      </p>
      {error && (
        <p className="text-xs text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
}

function Copyable({ value }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex items-center gap-2 mt-1">
      <input readOnly value={value} className="w-full border rounded px-2 py-1 text-xs" />
      <button
        type="button"
        className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100"
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
